import React from "react";
import { FormattedHTMLMessage, injectIntl } from "react-intl";
import { produce } from "immer";

import {
  LowIncomeStatusDefaultMessagesEnum,
  VerificationStepsEnum,
} from "../../lib/types/runtimeTypes";
import { hasFailedInstantMatch } from "../../lib/VerificationService/VerificationServiceHelpers";
import {
  FormSelectChoice,
  LowIncomePersonalInfoResponse,
  LowIncomePersonalInfoViewModel,
  LowIncomeStatus,
  Organization,
  State,
  VerificationService,
} from "../../lib/types/types";
import {
  getAvailableLowIncomeStatuses,
  getAvailableStateChoices,
  getStatusLabel,
  handleEmailOnKeyDown,
  orgToOption,
  updateFieldValidationErrorsByFieldId,
  updateViewModelOrganization,
} from "../../lib/utils/stepComponentHelpers/stepComponentHelpers";
import { BirthDateComponent as BirthDate } from "../FormFields/BirthDate/BirthDateComponent";
import { EmailComponent as Email } from "../FormFields/Email/EmailComponent";
import { PhoneNumberComponent as PhoneNumber } from "../FormFields/PhoneNumber/PhoneNumberComponent";
import { FirstNameComponent as FirstName } from "../FormFields/FirstName/FirstNameComponent";
import { LastNameComponent as LastName } from "../FormFields/LastName/LastNameComponent";
import { StatusComponent as LowIncomeStatusComponent } from "../FormFields/Status/StatusComponent";
import { LowIncomeOrganizationComponent as LowIncomeOrganization } from "../FormFields/LowIncomeOrganization/LowIncomeOrganizationComponent";
import { MarketConsentWrapperComponent as MarketConsentWrapper } from "../FormFields/MarketConsentWrapper/MarketConsentWrapperComponent";
import { PostalCodeComponent } from "../FormFields/PostalCode/PostalCodeComponent";
import { ChangeLocaleV2Component } from "../FormFields/ChangeLocale/ChangeLocaleComponent";
import { CountryComponentWrapper } from "../FormFields/Country/CountryComponentWrapper";
import { EBTCardNumberComponent } from "../FormFields/EBTCardNumber/EBTCardNumberComponent";
import { setViewModel } from "../../lib/VerificationService/ViewModel";
import { AddressComponent } from "../FormFields/Address/AddressComponent";
import { CityComponent } from "../FormFields/City/City";
import { StateSelectComponent } from "../FormFields/State/StateSelectComponent";
import { PersonalInfoWrapper } from "../PersonalInfoStepWrapper/PersonalInfoStepWrapper";
import { PersonalInfoStepSubmitButton } from "../PersonalInfoStepSubmitButton/PersonalInfoStepSubmitButton";
import { PersonalInfoHeader } from "../FormHeader/StepHeaders/PersonalInfoHeaderComponent";

import { FormFieldTypeahead } from "../FormFields/FormFieldTypeahead/FormFieldTypeahead";
import { getOrgSearchUrl } from "../../lib/ProgramTheme/programThemeGetters";

interface StepLowIncomePersonalInfoComponentProps {
  verificationService: VerificationService;
  intl: any;
}

type DefaultStatusMessages = {
  [key in LowIncomeStatus]: string;
};

export const defaultStatusMessages: DefaultStatusMessages = {
  SNAP_BENEFITS: "Snap Benefits Recipient",
  OTHER_GOVERNMENT_ASSISTANCE: "Other Government Assistance Recipient",
  COMMUNITY_ELIGIBILITY_PROVISION: "CEP (Community Eligibility Provision, Title 1/LAP)",
};

const StepLowIncomePersonalInfo = ({
  intl,
  verificationService,
}: StepLowIncomePersonalInfoComponentProps) => {
  const viewModel = verificationService.viewModel as LowIncomePersonalInfoViewModel;
  const { fieldValidationErrors } = verificationService;
  const verificationResponse =
    verificationService.verificationResponse as LowIncomePersonalInfoResponse;
  const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

  const locale = viewModel.localeChoice.value;

  const selectedStatus = viewModel.statuses[0] ? viewModel.statuses[0] : undefined;
  const isSelectedStatusSnapBenefits = selectedStatus === "SNAP_BENEFITS";
  const updateLowIncomeViewModel = (key: keyof LowIncomePersonalInfoViewModel, value: any) => {
    const nextState: LowIncomePersonalInfoViewModel = produce(
      viewModel,
      (draft: LowIncomePersonalInfoViewModel) => {
        (draft[key] as any) = value;
      },
    );
    verificationService.updateViewModel(nextState);
  };

  React.useEffect(() => {
    if (isSelectedStatusSnapBenefits) {
      const updatedViewModel = { fieldsToSkipValidation: ["organization"] };
      setViewModel(updatedViewModel);
    }
  }, [isSelectedStatusSnapBenefits]);
  return (
    <PersonalInfoWrapper
      verificationService={verificationService}
      step={verificationResponse.currentStep}
    >
      <PersonalInfoHeader
        verificationService={verificationService}
        classNameIdentifier="low-income"
      />

      <ChangeLocaleV2Component verificationService={verificationService} />
      <CountryComponentWrapper verificationService={verificationService} />

      <LowIncomeStatusComponent
        segmentId="low-income"
        value={{
          value: viewModel.statuses ? viewModel.statuses[0] : "",
          label: viewModel.statuses
            ? getStatusLabel(intl, viewModel.statuses[0], LowIncomeStatusDefaultMessagesEnum)
            : "",
        }}
        isErrored={!!fieldValidationErrors.statuses}
        options={getAvailableLowIncomeStatuses(intl, verificationResponse.availableStatuses)}
        onChange={(status: FormSelectChoice) => {
          const updatedStatuses = status ? [status.value as string] : [];
          updateLowIncomeViewModel("statuses", updatedStatuses);
          updateFieldValidationErrorsByFieldId(
            "statuses",
            status ? (status.value as string) : "",
            verificationService,
          );
        }}
      />
      {selectedStatus === "SNAP_BENEFITS" && (
        <EBTCardNumberComponent
          value={viewModel.ebtCardNumber}
          isErrored={!!fieldValidationErrors.ebtCardNumber}
          onChange={(newValue) => {
            updateLowIncomeViewModel("ebtCardNumber", newValue);
            updateFieldValidationErrorsByFieldId("ebtCardNumber", newValue, verificationService);
          }}
        />
      )}
      {selectedStatus === "OTHER_GOVERNMENT_ASSISTANCE" && (
        <LowIncomeOrganization
          isErrored={!!fieldValidationErrors.organization}
          onChange={(choice: Organization) => {
            updateViewModelOrganization(choice, verificationService);
            updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
          }}
          value={orgToOption(
            (verificationService.viewModel as LowIncomePersonalInfoViewModel).organization,
          )}
          verificationService={verificationService}
          isRequired
        />
      )}
      {selectedStatus === "COMMUNITY_ELIGIBILITY_PROVISION" && (
        <FormFieldTypeahead
          fieldId="cep-organization"
          value={viewModel.organization}
          verificationService={verificationService}
          onChange={(choice: Organization) => {
            updateViewModelOrganization(choice, verificationService);
            updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
          }}
          placeholder={intl.formatHTMLMessage({
            id: "schoolNamePlaceholder",
            defaultMessage: "School name",
          })}
          label={<FormattedHTMLMessage id="schoolName" defaultMessage="School name" />}
          errorMessage={
            !!fieldValidationErrors.organization && (
              <FormattedHTMLMessage
                id="errorId.invalidOrganization"
                defaultMessage="You must select an organization from the list"
              />
            )
          }
          isRequired={selectedStatus === "COMMUNITY_ELIGIBILITY_PROVISION"}
          orgSearchUrl={getOrgSearchUrl(
            verificationService.viewModel,
            verificationService.programTheme,
          )}
        />
      )}
      <div className="sid-names">
        <FirstName
          value={viewModel.firstName}
          isErrored={!!fieldValidationErrors.firstName}
          onChange={(newValue) => {
            updateLowIncomeViewModel("firstName", newValue);
            updateFieldValidationErrorsByFieldId("firstName", newValue, verificationService);
          }}
        />
        <LastName
          value={viewModel.lastName}
          isErrored={!!fieldValidationErrors.lastName}
          onChange={(newValue) => {
            updateLowIncomeViewModel("lastName", newValue);
            updateFieldValidationErrorsByFieldId("lastName", newValue, verificationService);
          }}
        />
      </div>
      <BirthDate
        isErrored={!!fieldValidationErrors.birthDate}
        errorId={fieldValidationErrors.birthDate}
        locale={locale}
        onChange={(newValue) => {
          updateLowIncomeViewModel("birthDate", newValue);
          updateFieldValidationErrorsByFieldId("birthDate", newValue, verificationService);
        }}
        value={viewModel.birthDate}
      />
      <AddressComponent
        isErrored={!!fieldValidationErrors.address1}
        errorId={fieldValidationErrors.address1}
        onChange={(newValue) => {
          updateLowIncomeViewModel("address1", newValue);
          updateFieldValidationErrorsByFieldId("address1", newValue, verificationService);
        }}
        value={viewModel.address1}
        isRequired
        explanation={
          <FormattedHTMLMessage id="addressExplanation" defaultMessage="Use your home address" />
        }
      />
      <div className="sid-address-extra-fields sid-thirds">
        <CityComponent
          isErrored={!!fieldValidationErrors.city}
          errorId={fieldValidationErrors.city}
          onChange={(newValue) => {
            updateLowIncomeViewModel("city", newValue);
            updateFieldValidationErrorsByFieldId("city", newValue, verificationService);
          }}
          value={viewModel.city}
          isRequired
        />
        <StateSelectComponent
          isRequired
          options={getAvailableStateChoices(verificationService.programTheme, intl)}
          value={viewModel.state || undefined}
          isErrored={Boolean(fieldValidationErrors.state)}
          onChange={async (stateChoice: FormSelectChoice<State, string>) => {
            const stateValue = stateChoice ? stateChoice.value : undefined;
            updateLowIncomeViewModel("state", stateValue);
            updateFieldValidationErrorsByFieldId("state", stateValue, verificationService);
          }}
        />
        <PostalCodeComponent
          isErrored={!!fieldValidationErrors.postalCode}
          onChange={(newValue) => {
            updateLowIncomeViewModel("postalCode", newValue);
            updateFieldValidationErrorsByFieldId("postalCode", newValue, verificationService);
          }}
          value={viewModel.postalCode}
        />
      </div>
      <Email
        value={viewModel.email}
        isErrored={!!fieldValidationErrors.email}
        explanation={<FormattedHTMLMessage id="emailExplanation" defaultMessage=" " />}
        onChange={(newValue) => {
          updateLowIncomeViewModel("email", newValue);
          updateFieldValidationErrorsByFieldId("email", newValue, verificationService);
        }}
        onKeyDown={(event) => handleEmailOnKeyDown(event)}
      />
      {verificationService.programTheme.isSmsNotifierConfigured ||
      verificationService.programTheme.smsLoopEnabled ? (
        <PhoneNumber
          isRequired={!!verificationService.programTheme.smsLoopEnabled}
          value={viewModel.phoneNumber}
          isErrored={!!fieldValidationErrors.phoneNumber}
          onChange={(newValue) => {
            updateLowIncomeViewModel("phoneNumber", newValue);
            updateFieldValidationErrorsByFieldId("phoneNumber", newValue, verificationService);
          }}
          selectedCountryCode={viewModel.countryChoice && viewModel.countryChoice.value}
        />
      ) : null}
      <MarketConsentWrapper
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.marketConsentValue}
        onChange={(newValue) => {
          updateLowIncomeViewModel("metadata", {
            ...verificationService.viewModel.metadata,
            marketConsentValue: newValue,
          });
          updateFieldValidationErrorsByFieldId("marketConsentValue", newValue, verificationService);
        }}
        viewModel={viewModel}
      />

      <PersonalInfoStepSubmitButton
        step={VerificationStepsEnum.collectLowIncomePersonalInfo}
        verificationService={verificationService}
        viewModel={viewModel}
        failedInstantMatch={failedInstantMatch}
        buttonText={
          <FormattedHTMLMessage id="verifyMyLowIncomeStatus" defaultMessage="Verify Status" />
        }
      />
    </PersonalInfoWrapper>
  );
};

export const StepLowIncomePersonalInfoComponent = injectIntl(StepLowIncomePersonalInfo);
